/**
 * WidgetNativeStudio — the ViewIR studio.
 *
 *   kind="standard"  the native widget CATALOG: one info card per authored
 *                    blueprint, in a grid split Public / Private. A card opens
 *                    the PREVIEW pane: the real runtime in a phone bezel + the
 *                    widget's real version ladder + the signal/slot graph.
 *   kind="bootstrap" the component library browser (registry.json).
 *
 * The selected widget rides in `?widget=<id>` so a preview is deep-linkable
 * and the browser back button does the obvious thing.
 *
 * ── WHAT THE CATALOG DOWNLOADS ───────────────────────────────────────────
 * Nothing but the list. Every card is drawn from `GET /widget-native/blueprints`
 * (accent, kinds, the three counts — the server reads them off the doc it is
 * already parsing), and the SELECTED blueprint is fetched on demand. The
 * catalog used to fan out over all nine blueprints in full (3.62 MB, scene
 * studio alone 2.0 MB) to draw nine rows — and did it even when the URL was
 * deep-linked straight into one widget.
 *
 * ── WHICH WIDGETS ARE PUBLIC ─────────────────────────────────────────────
 * The in-repo blueprint list is authoritative for "which native widgets exist";
 * the widget DB says which of them the app is actually being served. The two
 * are cross-joined on blueprint `id` === widget `slug`, and EACH DB call
 * degrades on its own (`.catch(() => [])`) — a 401 in the auth-sync window
 * leaves an un-sectioned catalog, never a blank page.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { nativeApi } from '../nativeApi';
import { widgetApi } from '../../../services/widgetApi';
import { registerRegistry } from '../viewir/registry';
import KitKeyframes from '../KitKeyframes';
import WidgetNativeTabs from './WidgetNativeTabs';
import NativePreviewPane from './NativePreviewPane';
import BootstrapBrowser from './BootstrapBrowser';
import { rememberStandardSearch } from './studioNav';

const C = { bg: '#f6f6f9', text: '#1c1c1e', dim: '#6e6e73', accent: '#6E59F2', card: '#fff', border: '#e2e2ea', warn: '#b26b00', warnBg: '#fff6e5' };

/** The kit's shimmer, in the studio's own palette (keyframe from KitKeyframes). */
const SHIMMER = {
  background: `linear-gradient(100deg, ${C.border} 30%, #f2f2f7 50%, ${C.border} 70%)`,
  backgroundSize: '200% 100%',
  animation: 'viNativeShimmer 1.2s linear infinite',
};

/** Join key: a blueprint id, a widget slug and a widget id are all the same slug. */
const keyOf = (v) => (v == null ? null : String(v).trim().toLowerCase() || null);

/**
 * One widget list, or nothing.
 *
 * Both halves of the join are optional decoration on a repo-driven grid, so
 * every failure mode collapses to the same answer: a 401 during the auth-sync
 * window, a 500 from a cold pool, or a build where the route isn't wired at
 * all. Mirrors WidgetCatalog's `.catch(() => ({ widgets: [] }))`.
 */
async function safeWidgets(call) {
  try {
    const r = await call();
    return Array.isArray(r?.widgets) ? r.widgets : [];
  } catch {
    return [];
  }
}

/** slug/id → widget row, first row wins. */
function indexWidgets(rows) {
  const byKey = new Map();
  for (const w of rows || []) {
    for (const k of [w?.slug, w?.id]) {
      const key = keyOf(k);
      if (key && !byKey.has(key)) byKey.set(key, w);
    }
  }
  return byKey;
}

const VISIBLE_FALLBACK_MS = 1200;

/**
 * "Has this card been on screen?" — one-way, and it never dies quietly.
 *
 * The version-labels call behind it is one request PER CARD, so it waits for
 * the card to be near the viewport rather than firing nine on mount. Two
 * degradations keep that from turning into a badge that silently never
 * appears:
 *   * no IntersectionObserver at all (a test DOM, an old WebView) → eager;
 *   * an observer that never delivers its FIRST entry. A live tab answers
 *     within a frame or two; a BACKGROUND/hidden tab throttles the observer
 *     (which is how Chrome automation drives a page), so "no callback at all
 *     by now" means the observer is dead, not that the card is off screen.
 */
function useOnVisible(ref) {
  const [seen, setSeen] = useState(() => typeof IntersectionObserver !== 'function');
  useEffect(() => {
    if (seen) return undefined; // no observer at all → the initial state is already `true`
    let answered = false;
    const io = new IntersectionObserver((entries) => {
      answered = true;
      if (entries.some((e) => e.isIntersecting)) setSeen(true);
    }, { rootMargin: '320px' }); // a card just under the fold is already loading
    // Nothing to observe (no node yet) leaves `answered` false, so the watchdog
    // below is the answer — the card loads rather than waiting forever.
    if (ref.current) io.observe(ref.current);
    const timer = setTimeout(() => { if (!answered) setSeen(true); }, VISIBLE_FALLBACK_MS);
    return () => { clearTimeout(timer); io.disconnect(); };
  }, [ref, seen]);
  return seen;
}

/**
 * The latest NATIVE version label on the ladder, once the card is visible.
 *
 * `undefined` = not asked yet or the ladder could not be read; `null` = the
 * widget has versions but none of them are native. Only a STRING can make a
 * pin look stale — an unknown ladder (401, no widget row in this env) is not
 * a stale pin, exactly as the preview pane's own attention rule has it.
 */
function useLatestNative(widgetKey, active) {
  const [latest, setLatest] = useState(undefined);
  useEffect(() => {
    if (!active || !widgetKey) return undefined;
    let alive = true;
    (async () => {
      try {
        const r = await widgetApi.listVersionLabels(widgetKey);
        const rows = Array.isArray(r?.versions) ? r.versions : [];
        // newest-first, so the first `ir` row IS the latest native one
        const native = rows.find((v) => v?.view_type === 'ir');
        if (alive) setLatest(native?.version_label || null);
      } catch { /* unknown ladder ≠ stale pin */ }
    })();
    return () => { alive = false; };
  }, [widgetKey, active]);
  return latest;
}

/** "3 kinds · 4 pages · 22 components · 9 connections", from the LIST fields. */
function metaLine(meta) {
  const bits = [];
  const kinds = Array.isArray(meta.kinds) ? meta.kinds : [];
  if (kinds.length) bits.push(kinds.join(', '));
  const counts = [
    [meta.page_count, 'page'], [meta.component_count, 'component'], [meta.connection_count, 'connection'],
  ];
  for (const [n, noun] of counts) {
    if (Number.isFinite(n)) bits.push(`${n} ${noun}${n === 1 ? '' : 's'}`);
  }
  return bits.length ? bits.join(' · ') : '—';
}

function Badge({ label, title, color }) {
  return (
    <span
      title={title}
      style={{
        background: `${color}22`, color, fontSize: 9, fontWeight: 700, letterSpacing: '0.06em',
        textTransform: 'uppercase', padding: '2px 8px', borderRadius: 999, border: `1px solid ${color}55`,
      }}
    >{label}</span>
  );
}

/**
 * ONE INFO CARD.
 *
 * Everything on it except the attention chip is drawn from data the catalog
 * already has. The chip is the one lazy part: it needs this widget's version
 * ladder, so it is fetched when the card comes into view and the card renders
 * without it until then.
 */
function WidgetCard({ meta, widget, isMine, isShared, onOpen }) {
  const host = useRef(null);
  const visible = useOnVisible(host);
  const widgetKey = widget?.id != null ? String(widget.id) : meta.id;
  const latestNative = useLatestNative(widgetKey, visible);

  const release = widget?.tags?.release || null;
  const pinLabel = release?.version_label || null;
  const pinIsNative = release?.bundle_manifest?.view_type === 'ir';
  // Stale exactly when the ladder ANSWERED with a native version and Public is
  // pinned to something else — an older native label, a webview one, or nothing.
  const attention = Boolean(latestNative && pinLabel !== latestNative);
  const attentionText = !pinLabel
    ? `No native version pinned to Public — the latest native version is ${latestNative}.`
    : `Public is pinned to ${pinLabel}${pinIsNative ? '' : ' (webview)'} — the latest native version is ${latestNative}.`;

  return (
    <div
      ref={host}
      data-testid={`native-card-${meta.id}`}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } }}
      style={{
        position: 'relative', background: C.card, border: `1px solid ${C.border}`, borderRadius: 12,
        padding: 12, display: 'flex', flexDirection: 'column', gap: 10, cursor: 'pointer',
        textAlign: 'left', color: C.text, transition: 'all 0.15s',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = `${C.accent}66`; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.border; }}
    >
      {attention ? (
        <span
          data-testid={`native-card-attention-${meta.id}`}
          title={attentionText}
          style={{
            position: 'absolute', top: 8, right: 10, zIndex: 2,
            background: C.warnBg, color: C.warn, border: `1px solid ${C.warn}55`,
            fontSize: 10, fontWeight: 700, borderRadius: 999, padding: '2px 7px', cursor: 'help',
          }}
        >⚠</span>
      ) : null}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
        <span style={{
          width: 40, height: 40, borderRadius: 12, flex: '0 0 40px',
          background: meta.accent || '#ccc',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontWeight: 800, fontSize: 15,
        }}>{(meta.title || meta.id).slice(0, 1).toUpperCase()}</span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 14, fontWeight: 700 }}>{meta.title || meta.id}</span>
          <code style={{ display: 'block', fontSize: 11, color: C.dim, marginTop: 2 }}>{meta.id}</code>
        </span>
        <span style={{ color: C.dim, fontSize: 18, flex: '0 0 auto' }}>›</span>
      </div>

      <div style={{ fontSize: 11.5, color: C.dim, lineHeight: 1.5 }}>{metaLine(meta)}</div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 11, color: C.dim }}>
        {pinLabel
          ? <span>Public pin <code>{pinLabel}</code>{pinIsNative ? '' : ' (webview)'}</span>
          : <span>Not pinned to Public</span>}
        {isMine ? <Badge label="Mine" title="You own this widget" color={C.accent} /> : null}
        {isShared ? <Badge label="Shared" title="A teammate's private widget — shared with admins" color={C.warn} /> : null}
      </div>
    </div>
  );
}

/** A titled grid of info cards — the WebView catalog's GridSection, natively. */
function GridSection({ id, title, hint, cards, onOpen }) {
  return (
    <section data-testid={`native-catalog-${id}`} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <header style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>{title}</h3>
        <span style={{ fontSize: 11, color: C.dim }}>{cards.length} widget{cards.length === 1 ? '' : 's'}</span>
      </header>
      {cards.length === 0 ? (
        <div style={{ fontSize: 12, color: C.dim, padding: '20px 4px' }}>{hint}</div>
      ) : (
        <div
          data-testid={`native-catalog-grid-${id}`}
          style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(264px, 1fr))', gap: 12 }}
        >
          {cards.map((c) => (
            <WidgetCard
              key={c.meta.id}
              meta={c.meta}
              widget={c.widget}
              isMine={c.isMine}
              isShared={c.isShared}
              onOpen={() => onOpen(c.meta.id)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

/** The grid, before the join can say which card belongs where. */
function CatalogSkeleton({ count }) {
  return (
    <div
      data-testid="native-catalog-loading"
      style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(264px, 1fr))', gap: 12 }}
    >
      {Array.from({ length: Math.max(count, 1) }, (_, i) => (
        <div key={i} style={{ ...SHIMMER, height: 118, borderRadius: 12 }} />
      ))}
    </div>
  );
}

function NativeCatalog({ list, onOpen }) {
  // null until BOTH calls have settled (each degrading to []). The sections are
  // drawn once: a card that appears under Private and then hops to Public when
  // the join lands is a jumping page — and, because the hop re-parents the card,
  // a second version-labels fetch for the same widget.
  const [db, setDb] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      const [mine, pub] = await Promise.all([
        safeWidgets(() => widgetApi.listMine()),
        // The studio is an authoring surface → the FULL public set, not the
        // curated app "Discover" subset.
        safeWidgets(() => widgetApi.listPublic(50, 0, { scope: 'all' })),
      ]);
      if (alive) setDb({ mine, pub });
    })();
    return () => { alive = false; };
  }, []);

  const [publicCards, privateCards] = useMemo(() => {
    const mineIdx = indexWidgets(db?.mine);
    const pubIdx = indexWidgets(db?.pub);
    const pubs = [];
    const privs = [];
    for (const meta of list || []) {
      const key = keyOf(meta.id);
      const own = key ? mineIdx.get(key) : null;
      const published = key ? pubIdx.get(key) : null;
      // The owner row carries the full manifest (the public one is stripped but
      // still carries version_label + view_type), so prefer it for the pin.
      const widget = own || published || null;
      const card = {
        meta,
        widget,
        isMine: Boolean(own && !own.shared),
        isShared: Boolean(own?.shared),
      };
      // A public widget lists ONCE, under Public. A blueprint with no widget
      // row at all is repo-only — private by definition, not missing.
      if (own?.is_public || published) pubs.push(card);
      else privs.push(card);
    }
    return [pubs, privs];
  }, [list, db]);

  if (!db) return <CatalogSkeleton count={(list || []).length} />;

  return (
    <>
      <GridSection
        id="public"
        title="Public"
        cards={publicCards}
        onOpen={onOpen}
        hint="No native widget is pinned to Public yet — publish one from its Versions tab."
      />
      <GridSection
        id="private"
        title="Private"
        cards={privateCards}
        onOpen={onOpen}
        hint="Every authored blueprint is already public."
      />
    </>
  );
}

export default function WidgetNativeStudio({ kind = 'standard' }) {
  const [params, setParams] = useSearchParams();
  const selectedId = params.get('widget');
  const search = params.toString();

  // Park the standard tab's full URL state so a hop to Component Bootstrap and
  // back doesn't reset it (the route unmounts; the URL is the only state).
  useEffect(() => {
    if (kind === 'standard') rememberStandardSearch(search);
  }, [kind, search]);

  const [registry, setRegistry] = useState(null);
  const [list, setList] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [reg, l] = await Promise.all([nativeApi.registry(), nativeApi.listBlueprints()]);
        if (!alive) return;
        // The SERVER registry is the contract, so install it: its `composition`
        // bodies, prop specs, fallbacks and `introduced_in_app` gates override
        // the bundled defaults. This is the hot-update path made real — an L3
        // Domain composition edited on the server renders here with no rebuild.
        registerRegistry(reg);
        setRegistry(reg);
        setList(l?.blueprints || []);
      } catch (e) {
        if (alive) setError(e.message || String(e));
      }
    })();
    return () => { alive = false; };
  }, []);

  // THE SELECTED BLUEPRINT, AND ONLY IT. `nativeApi` memoises per path, so
  // re-entering a widget (back → open again) costs nothing.
  const [loaded, setLoaded] = useState(null); // { id, doc }
  useEffect(() => {
    if (!selectedId) return undefined;
    let alive = true;
    (async () => {
      try {
        const doc = await nativeApi.blueprint(selectedId);
        if (alive) setLoaded({ id: selectedId, doc });
      } catch (e) {
        if (alive) setError(e.message || String(e));
      }
    })();
    return () => { alive = false; };
  }, [selectedId]);

  // The registry is installed before anything renders from it, so the preview
  // waits for both — a blueprint drawn against the bundled defaults and then
  // re-drawn against the server's would flash a different widget.
  const selected = selectedId && registry && loaded?.id === selectedId ? loaded.doc : null;
  const header = useMemo(() => (
    <header style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
      <WidgetNativeTabs active={kind} colors={{ accent: C.accent, text: C.text, bgCard: C.card, border: C.border }} />
    </header>
  ), [kind]);

  return (
    <div style={{
      padding: '24px 32px', height: '100%', minHeight: 0, overflowY: 'auto',
      background: C.bg, color: C.text, display: 'flex', flexDirection: 'column', gap: 20,
    }}>
      <KitKeyframes />
      {header}

      {error ? (
        <div style={{
          fontSize: 13, color: '#b3261e', background: '#fff1f0', border: '1px solid #ffd0cc',
          borderRadius: 10, padding: 14, lineHeight: 1.5,
        }}>
          Couldn’t load the native framework: {error}
          <div style={{ color: C.dim, marginTop: 6, fontSize: 12 }}>
            <code>/api/widget-native/*</code> serves <code>backend/widget-native/</code> straight off the repo —
            a 404 means the api-server can’t see that directory (set <code>WIDGET_NATIVE_DIR</code>).
          </div>
        </div>
      ) : null}

      {kind === 'bootstrap' ? (
        <>
          <div>
            <h2 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 6px' }}>Component Bootstrap</h2>
            <p style={{ fontSize: 13, color: C.dim, margin: 0, maxWidth: 820, lineHeight: 1.55 }}>
              The native component library, L0 Foundations → L4 Patterns. Each card is a live render of the
              web kit plus its contract: what the component SEALS (a widget cannot change it) and what it
              lets you override. Both runtimes implement this same registry, which is what makes a blueprint
              portable between iOS and web.
            </p>
          </div>
          {registry ? <BootstrapBrowser registry={registry} locate={params.get('component')} /> : (!error && <div style={{ color: C.dim, fontSize: 13 }}>Loading registry…</div>)}
        </>
      ) : selected ? (
        <NativePreviewPane
          blueprint={selected}
          registry={registry}
          showFab={(list || []).find((c) => c.id === selectedId)?.show_fab !== false}
          onBack={() => { params.delete('widget'); setParams(params, { replace: true }); }}
        />
      ) : selectedId && !error ? (
        <div data-testid="native-blueprint-loading" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 13, color: C.dim }}>Loading <code>{selectedId}</code>…</div>
          <div style={{ ...SHIMMER, width: 300, height: 420, borderRadius: 20 }} />
        </div>
      ) : (
        <>
          <div>
            <h2 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 6px' }}>Native Widgets</h2>
            <p style={{ fontSize: 13, color: C.dim, margin: 0, maxWidth: 820, lineHeight: 1.55 }}>
              Widgets rendered from a declarative ViewIR blueprint instead of an HTML bundle — no per-widget
              JS, no bridge code. Open one to see the real runtime in a phone frame, its live version ladder,
              and the signal/slot graph its <code>connections[]</code> describe.
            </p>
          </div>
          {list && list.length ? (
            <NativeCatalog list={list} onOpen={(id) => { params.set('widget', id); setParams(params); }} />
          ) : null}
          {list && list.length === 0 ? (
            <div style={{ color: C.dim, fontSize: 13 }}>No blueprints authored yet.</div>
          ) : null}
          {!list && !error ? <div style={{ color: C.dim, fontSize: 13 }}>Loading blueprints…</div> : null}
        </>
      )}
    </div>
  );
}
